import { Select, SelectOption } from "@justworkshr/milo-form";
import { ChangeEvent, ReactElement, useContext } from "react";
import { AdditionalPayContext } from "pages/employer-of-record/payments/contexts/additionalPayContext";
import {
  SupportedCountriesContext,
  SupportedCountriesDataType,
} from "pages/employer-of-record/contexts/supportedCountriesContext";

import { CountryCode } from "pages/employer-of-record/onboarding/constants";
import { AdditionalPayFormData, FormPayloadType } from "../../types";
import { useFormikContext } from "formik";
import { useParams } from "react-router-dom";
import { getFormInitialValues } from "pages/employer-of-record/payments/utils";
import { getEnvironmentName } from "lib/environment";
import { getSortedCountries } from "./util";
import { useMemberData } from "pages/employer-of-record/payments/contexts/memberDataContext";

function CountrySelect(): ReactElement {
  const additionalPayContext = useContext(AdditionalPayContext);
  const memberDataContext = useMemberData();
  const formikContext =
    useFormikContext<AdditionalPayFormData<FormPayloadType>>();
  const { handleChange, setValues, setTouched } = formikContext;

  const { formType } = useParams();
  const formInitialValues = getFormInitialValues(formType || "");

  const onChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
    handleChange(event);
    additionalPayContext?.setSelectedCountry(event.target.value);
    formInitialValues.setUp.workCountry = event.target.value as CountryCode;
    setValues(() => formInitialValues);
    setTouched({});
  };

  const { supportedCountriesData } = useContext(SupportedCountriesContext);
  const memberByCountryCount = memberDataContext?.memberCountryCount || {};

  // Filter supported countries based on productionCountries array if in production mode
  const isProduction =
    getEnvironmentName() === "production" || getEnvironmentName() === "tour";
  const productionCountries = [
    CountryCode.AU,
    CountryCode.BR,
    CountryCode.CA,
    CountryCode.CL,
    CountryCode.CO,
    CountryCode.CR,
    CountryCode.DE,
    CountryCode.DK,
    CountryCode.EE,
    CountryCode.ES,
    CountryCode.FR,
    CountryCode.GB,
    CountryCode.GR,
    CountryCode.IE,
    CountryCode.IL,
    CountryCode.IN,
    CountryCode.IT,
    CountryCode.NL,
    CountryCode.MX,
    CountryCode.MY,
    CountryCode.PE,
    CountryCode.PH,
    CountryCode.PL,
    CountryCode.PT,
    CountryCode.RO,
    CountryCode.SE,
    CountryCode.SG,
    CountryCode.TR,
    CountryCode.ZA,
  ];
  let filteredCountries = isProduction
    ? Object.values(supportedCountriesData).filter((country) =>
        productionCountries.includes(country.code as CountryCode)
      )
    : Object.values(supportedCountriesData);

  filteredCountries = getSortedCountries(
    filteredCountries,
    memberByCountryCount
  );

  return (
    <Select
      name="workCountry"
      placeholder="Select"
      value={formikContext.values.setUp.workCountry}
      onChange={onChangeHandler}
      disabled={additionalPayContext?.isEdit}
    >
      {Object.values(filteredCountries).map(
        (country: SupportedCountriesDataType) => {
          const count = memberByCountryCount[country.code] || 0;
          const memberCount = !additionalPayContext?.isEdit
            ? `(${count} members)`
            : "";
          const option = `${country.commonName} ${memberCount}`;
          return (
            <SelectOption
              value={country.code}
              key={country.code}
              disabled={count === 0}
            >
              {option}
            </SelectOption>
          );
        }
      )}
    </Select>
  );
}

export default CountrySelect;
